import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy,
  WhatsApp,
  Map as MapIcon,
  Apartment as ApartmentIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { signedFetch } from '../../services/api';
import { logShareLinkCreated } from '../../services/analytics';

/**
 * Modules the share invite can grant access to. Mirrors the backend enum in
 * `backend/src/entities/TransformationPickerExternalUser.ts`.
 */
export type ShareExternalUserModule =
  | 'PICKER_SITE_PLANS'
  | 'PICKER_PROJECT_DETAILS'
  | 'PICKER_PROJECT_RESEARCH';

interface ShareExternalUserDialogProps {
  open: boolean;
  onClose: () => void;
  /** Page the recipient will be allowed to access. */
  module: ShareExternalUserModule;
  /**
   * Project this share is scoped to. Required when `module === 'PICKER_PROJECT_DETAILS'`
   * (the backend rejects the request otherwise); ignored for the other modules.
   */
  projectId?: string | null;
  /**
   * Optional display label used in the dialog copy + WhatsApp message. e.g.
   * `"The Reserve Residences"` for project details / project research, or the
   * generic `"Site Plans"` heading for the index.
   */
  subjectLabel?: string;
}

/**
 * Inline Telegram glyph used as a button start-icon. MUI's icon set does
 * not ship a Telegram mark, so we hand-roll one matching the brand
 * paper-plane silhouette. Stroke / fill is `currentColor` so the
 * surrounding `<Button>` colour cascade applies.
 */
const TelegramIcon: React.FC<{ fontSize?: 'small' | 'medium' | 'large' }> = ({
  fontSize = 'small',
}) => {
  const size = fontSize === 'large' ? 28 : fontSize === 'medium' ? 24 : 20;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
    </svg>
  );
};

const MODULE_LABEL: Record<ShareExternalUserModule, string> = {
  PICKER_SITE_PLANS: 'Site Plans',
  PICKER_PROJECT_DETAILS: 'Project Details',
  PICKER_PROJECT_RESEARCH: 'Project Research',
};

/** Icon shown next to the "You are sharing" badge in the dialog body. */
const MODULE_ICON: Record<ShareExternalUserModule, React.ReactNode> = {
  PICKER_SITE_PLANS: <MapIcon fontSize="small" />,
  PICKER_PROJECT_DETAILS: <ApartmentIcon fontSize="small" />,
  PICKER_PROJECT_RESEARCH: <SearchIcon fontSize="small" />,
};

/**
 * Dialog used on the Site Plans, Project Details, and Project Research pages
 * to issue a one-shot share link bound to a recipient. Calls
 * `POST /api/transformation-picker-external-users`, then surfaces the
 * generated URL with Copy / WhatsApp helpers (modelled after the existing
 * `ShareProjectDialog`).
 */
export const ShareExternalUserDialog: React.FC<ShareExternalUserDialogProps> = ({
  open,
  onClose,
  module,
  projectId,
  subjectLabel,
}) => {
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Reset form whenever the dialog is opened anew.
  useEffect(() => {
    if (open) {
      setName('');
      setMobile('');
      setError(null);
      setShareUrl(null);
    }
  }, [open]);

  const moduleLabel = MODULE_LABEL[module];
  const successLabel = subjectLabel || moduleLabel;
  const titleLabel = subjectLabel ? `${moduleLabel} — ${subjectLabel}` : moduleLabel;

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Please enter the recipient’s name.');
      return;
    }
    const trimmedMobile = mobile.trim();
    const mobileDigits = trimmedMobile.replace(/\D+/g, '');
    if (mobileDigits.length < 8) {
      setError('Please enter a valid mobile number.');
      return;
    }
    if (module === 'PICKER_PROJECT_DETAILS' && !projectId) {
      setError('Project information is missing — please re-open the dialog.');
      return;
    }

    setLoading(true);
    try {
      const response = await signedFetch('/api/transformation-picker-external-users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: trimmedName,
          mobile: trimmedMobile,
          module,
          projectId: module === 'PICKER_PROJECT_DETAILS' ? projectId : null,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          (typeof payload?.error === 'string' && payload.error) ||
            `Failed to create share link (${response.status}).`,
        );
      }
      const uuid = typeof payload?.uuid === 'string' ? payload.uuid : '';
      if (!uuid) throw new Error('Backend did not return a share uuid.');
      // Prefer the backend's shortened URL — it was already produced
      // (and persisted) during create. Fall back to the long `fullUrl`
      // the backend always emits when the shortener could not produce a
      // valid short link. The frontend never needs to know the public
      // host — that's owned by the backend's `PUBLIC_SITE_URL` env var.
      const backendShortUrl =
        typeof payload?.shortUrl === 'string' && payload.shortUrl.trim().length > 0
          ? payload.shortUrl.trim()
          : '';
      const backendFullUrl =
        typeof payload?.fullUrl === 'string' && payload.fullUrl.trim().length > 0
          ? payload.fullUrl.trim()
          : '';
      const displayShareUrl = backendShortUrl || backendFullUrl;
      if (!displayShareUrl) {
        throw new Error('Backend did not return a share URL.');
      }
      setShareUrl(displayShareUrl);
      // Fire the share-link analytics event *after* we know the uuid is
      // valid — captures the issuing agent (CEA, attached automatically
      // from the analytics cache), the recipient mobile the agent typed,
      // the share uuid, and the module being shared. Errors are
      // swallowed inside `safeLogEvent` so a failed analytics call can
      // never break the share flow.
      logShareLinkCreated({
        module,
        mobile: trimmedMobile,
        uuid,
        projectId: module === 'PICKER_PROJECT_DETAILS' ? projectId ?? null : null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create share link.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setSnackbarMessage('Link copied to clipboard!');
    } catch {
      setSnackbarMessage('Unable to access clipboard. Long-press the link to copy.');
    }
    setSnackbarOpen(true);
  };

  /**
   * Build the prefilled message used by every external share intent.
   * Centralised so WhatsApp and Telegram (and any future channel) emit
   * the same copy. Site Plans + Project Research share the product-led
   * Super Picker pitch (both surface the broader tool); Project Details
   * leads with the specific project name.
   */
  const buildShareMessage = (): string => {
    const greeting = name.trim() ? `Hi ${name.trim()}` : 'Hi';
    if (module === 'PICKER_PROJECT_DETAILS') {
      return `${greeting}, here's the latest on ${successLabel}. Tap to explore: ${shareUrl}`;
    }
    // PICKER_SITE_PLANS + PICKER_PROJECT_RESEARCH — same Super Picker pitch.
    return `${greeting}, check out Super Picker — our smart tool to help you find the home that best fits your needs: ${shareUrl}`;
  };

  const handleWhatsAppShare = () => {
    if (!shareUrl) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(buildShareMessage())}`, '_blank');
  };

  /**
   * Open Telegram's "share to chat" intent. Telegram's `share/url` page
   * takes the URL as `?url=` and an optional `&text=` for caption — we
   * pass only the message (which already contains the URL) as the text
   * so the recipient sees the exact greeting; we still pass `?url=` for
   * Telegram clients that prefer to render a link preview.
   */
  const handleTelegramShare = () => {
    if (!shareUrl) return;
    const message = buildShareMessage();
    const params = new URLSearchParams({ url: shareUrl, text: message });
    window.open(`https://t.me/share/url?${params.toString()}`, '_blank');
  };

  const success = shareUrl != null;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, bgcolor: 'background.paper' } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
          }}
        >
          {success ? 'Share Link Created' : `Share ${titleLabel}`}
          <IconButton onClick={handleClose} size="small" disabled={loading}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 3, bgcolor: 'background.default' }}>
          {/* Module badge — shown in both the form and success states so
              the agent always sees which page the recipient will land on.
              Tracks the subjectLabel (e.g. project name) when provided. */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.75,
              mb: 2,
              borderRadius: 999,
              bgcolor: 'rgba(var(--color-accent-rgb, 232, 160, 70), 0.12)',
              border: '1px solid rgba(var(--color-accent-rgb, 232, 160, 70), 0.4)',
              color: 'var(--color-text-strong)',
              maxWidth: '100%',
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                color: 'var(--color-accent)',
              }}
            >
              {MODULE_ICON[module]}
            </Box>
            <Typography
              component="span"
              sx={{
                fontSize: '0.85rem',
                fontWeight: 500,
                opacity: 0.75,
                lineHeight: 1,
              }}
            >
              You are sharing:
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: '0.95rem',
                fontWeight: 700,
                lineHeight: 1,
                color: 'var(--color-accent)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 280,
              }}
            >
              {moduleLabel}
              {subjectLabel ? (
                <Box
                  component="span"
                  sx={{
                    opacity: 0.7,
                    fontWeight: 500,
                    ml: 0.5,
                    color: 'var(--color-text-strong)',
                  }}
                >
                  {` — ${subjectLabel}`}
                </Box>
              ) : null}
            </Typography>
          </Box>

          {!success ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create a personalized link to share <strong>{successLabel}</strong> with your client.
                They can open the link without any login.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                label="Recipient Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
                placeholder="John Doe"
                sx={{ mb: 2 }}
                disabled={loading}
                helperText="Required"
              />

              <TextField
                label="Mobile Number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                fullWidth
                required
                placeholder="9123 4567"
                sx={{ mb: 1 }}
                disabled={loading}
                helperText="Required"
              />
            </Box>
          ) : (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                Share link created successfully!
              </Alert>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Share this link with your client:
              </Typography>

              <Box
                sx={{
                  p: 2,
                  bgcolor: 'grey.900',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'grey.700',
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  color: 'common.white',
                }}
              >
                {shareUrl}
              </Box>

              {/* Copy-link sits on its own row at the top — it's the
                  channel-agnostic primary action. The two messenger
                  intents share the second row, equally weighted. */}
              <Button
                variant="contained"
                fullWidth
                startIcon={<ContentCopy />}
                onClick={handleCopyToClipboard}
                color="primary"
                sx={{ mt: 3 }}
              >
                Copy Link
              </Button>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<WhatsApp />}
                  onClick={handleWhatsAppShare}
                  sx={{
                    color: '#25D366',
                    borderColor: '#25D366',
                    '&:hover': {
                      borderColor: '#128C7E',
                      bgcolor: 'rgba(37, 211, 102, 0.04)',
                    },
                  }}
                >
                  WhatsApp
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<TelegramIcon />}
                  onClick={handleTelegramShare}
                  sx={{
                    // Telegram brand blue (#229ED9). Hover darkens slightly
                    // to match the WhatsApp button's interaction model.
                    color: '#229ED9',
                    borderColor: '#229ED9',
                    '&:hover': {
                      borderColor: '#1A85B6',
                      bgcolor: 'rgba(34, 158, 217, 0.04)',
                    },
                  }}
                >
                  Telegram
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          {!success ? (
            <>
              <Button onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={loading}
                color="primary"
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Creating…
                  </>
                ) : (
                  'Create Share Link'
                )}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose} variant="contained" color="primary">
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default ShareExternalUserDialog;
